/**
 * The seam between the UI and the backend (mock or real AWS).
 *
 * The UI only ever talks to these interfaces, so swapping the mock
 * implementation for the Cognito/S3-backed one is a single wiring change.
 */

import rolesArtifact from '../config/roles.json';

/**
 * Employee role groups (from Cognito `cognito:groups`). Suppliers have no
 * employee group. The role vocabulary and the role→area mapping are governed by
 * a single artifact (src/config/roles.json); the Python token trigger reads a
 * matching copy, so the two languages can't drift.
 *  - admin             → all areas (Connect + Control + Insights)
 *  - supplier-control  → Control + Insights
 *  - supplier-insights → Insights
 */
export type UserRole = 'admin' | 'supplier-control' | 'supplier-insights';

/** Top-level app areas a signed-in user can reach. */
export type AppArea = 'connect' | 'control' | 'insights';

/**
 * Version of the role→area Shared Kernel this build compiled in. Surfaced so it
 * can be compared against the token trigger's `kernel_version` to detect a
 * rollout where the two deployables disagree on the kernel.
 */
export const KERNEL_VERSION: string = rolesArtifact.version;

export interface SupplierUser {
    email: string;
    company: string;
    /**
     * Employee role groups. Empty/undefined for password suppliers (who only
     * reach the Connect area).
     */
    roles?: UserRole[];
    /**
     * This supplier's M2M OAuth client id for the ASN API, provisioned at
     * admin approval. Safe to display (it identifies the client, like a
     * username). The matching clientSecret is issued once at approval and is
     * never exposed to the app.
     */
    clientId?: string;
}

/**
 * Areas a user can access, derived from their roles. The mapping is data-driven
 * from the governed roles artifact (not a hand-maintained branch), so it stays
 * in step with the Python token trigger:
 *  - admin             → Connect + Control + Insights (everything)
 *  - supplier-control  → Control + Insights
 *  - supplier-insights → Insights
 *  - none (supplier)   → Connect only
 */
export function areasFor(roles: UserRole[] | undefined): AppArea[] {
    const areaOrder = rolesArtifact.areas as AppArea[];
    const byName = new Map<string, string[]>(rolesArtifact.roles.map((r) => [r.name, r.areas]));
    const granted = new Set<string>((roles ?? []).flatMap((role) => byName.get(role) ?? []));
    if (granted.size === 0) return rolesArtifact.defaultAreas as AppArea[];
    return areaOrder.filter((area) => granted.has(area));
}

export interface SignUpParams {
    email: string;
    password: string;
    company: string;
}

/** Counts for one integration type over a window. */
export interface IntegrationCounts {
    total: number;
    dataErrors: number;
    businessErrors: number;
}

/** Per-integration ASN metrics for a time window (all suppliers, ops view). */
export interface AsnMetrics {
    from: string;
    to: string;
    /** Keyed by integration type: 'API' | 'PRESIGNED_URL' | 'FILE_DROP'. */
    byIntegration: Record<string, IntegrationCounts>;
}

export interface AsnMetricsService {
    /**
     * Per-integration ASN counts over [from, to] (ISO-8601). Omit both for the
     * service default window.
     */
    getMetrics(from?: string, to?: string): Promise<AsnMetrics>;
}

/**
 * Raised when sign-in is rejected because the account exists but is not yet
 * approved by an admin (disabled after email confirmation).
 */
export class PendingApprovalError extends Error {
    constructor(message = 'Your account is awaiting approval.') {
        super(message);
        this.name = 'PendingApprovalError';
    }
}

/**
 * Result of a password sign-in: either fully signed in, or an email MFA code
 * is required before a session is issued.
 */
export type SignInResult =
    | { status: 'SIGNED_IN'; user: SupplierUser }
    | { status: 'EMAIL_MFA_REQUIRED' };

export interface AuthService {
    /**
     * Register a new supplier. Returns once the user pool has accepted the
     * sign-up and (for real Cognito) dispatched a verification email.
     */
    signUp(params: SignUpParams): Promise<void>;

    /**
     * Confirm a sign-up using the code emailed to the supplier.
     */
    confirmSignUp(email: string, code: string): Promise<void>;

    /**
     * Sign in with email + password. Resolves to a signed-in user or an MFA
     * challenge, or throws PendingApprovalError if the account isn't approved.
     */
    signIn(email: string, password: string): Promise<SignInResult>;

    /**
     * Complete an email-MFA sign-in with the code emailed to the supplier.
     * The email is used as a fallback for the returned user when Cognito
     * attributes are not yet readable post-MFA.
     */
    confirmSignIn(code: string, email?: string): Promise<SupplierUser>;

    /**
     * Re-issue an email MFA code by re-initiating sign-in. Used when the first
     * code is delayed or has expired. Expects to land back on the email-MFA
     * challenge; throws if the pool returns an unexpected step.
     */
    resendMfaCode(email: string, password: string): Promise<void>;

    /**
     * Begin self-service password reset: Cognito emails a verification code to
     * the account's email. Resolves once the code has been dispatched.
     */
    resetPassword(email: string): Promise<void>;

    /**
     * Complete a password reset with the emailed code and the new password.
     */
    confirmResetPassword(email: string, code: string, newPassword: string): Promise<void>;

    /**
     * Start employee SSO via the Cognito Hosted UI (Midway OIDC). Redirects the
     * browser; the session is picked up on return. No-op if SSO isn't enabled.
     */
    signInWithSso(): Promise<void>;

    /**
     * Sign out the current session.
     */
    signOut(): Promise<void>;

    /**
     * The currently authenticated user, or null if signed out.
     */
    getCurrentUser(): Promise<SupplierUser | null>;
}

export interface UploadService {
    /**
     * Upload a file on behalf of the signed-in supplier. Uploads land under a
     * per-supplier key prefix in a private S3 bucket.
     */
    uploadFile(file: File, onProgress?: (percent: number) => void): Promise<void>;
}

/** A supplier item and its editable lead-time attributes. */
export interface LeadTimeItem {
    item: string;
    desc: string;
    /** Current lead time in days. */
    current: number;
    /** ISO country code (read-only). */
    country: string;
    endOfSale: string;
    endOfLife: string;
    hazardClass: string;
    dgGoods: boolean;
}

/** One audit-history entry for a lead-time item (fields blank if unchanged). */
export interface LeadTimeHistoryEntry {
    when: string;
    leadTime: string;
    eos: string;
    eol: string;
    hazard: string;
    by: string;
}

/** A single item's attribute change to persist. */
export interface LeadTimeChange {
    item: string;
    leadTime?: number | null;
    endOfSale?: string;
    endOfLife?: string;
    hazardClass?: string;
    dgGoods?: boolean;
}

export interface LeadTimeService {
    /** Supplier item catalog with current attribute values. */
    listItems(): Promise<LeadTimeItem[]>;
    /** Audit history for a single item. */
    getHistory(item: string): Promise<LeadTimeHistoryEntry[]>;
    /** Persist a batch of attribute changes. */
    saveChanges(changes: LeadTimeChange[]): Promise<void>;
}

// ── Supplier Control (internal access management) ─────────────────────────────

/** A supplier site (physical location) under a supplier organization. */
export interface ControlSite {
    id: string;
    /** Business site code leadership recognizes (e.g. DEN001). */
    code: string;
    name: string;
    location: string;
}

/** A supplier login and which site ids it can access. */
export interface ControlLogin {
    email: string;
    /** Map of siteId → granted. */
    access: Record<string, boolean>;
}

/** A supplier organization with its sites and logins. */
export interface ControlSupplier {
    id: string;
    name: string;
    sites: ControlSite[];
    logins: ControlLogin[];
}

/** A pending supplier-login access request awaiting approval. */
export interface ControlRequest {
    id: string;
    email: string;
    supplier: string;
    /** Number of sites available for the supplier. */
    sites: number;
    submitted: string;
    /** True once the requester has verified their email. */
    verified: boolean;
}

/** How a user authenticates — for the ops Users view. */
export type UserType = 'supplier' | 'employee';

/** A user pool account, classified by type for ops visibility. */
export interface ControlUser {
    username: string;
    email: string;
    /** supplier = password sign-up; employee = federated (Midway) SSO. */
    type: UserType;
    /** Role groups (employees only); empty for suppliers. */
    roles: string[];
    enabled: boolean;
    status: string;
    created: string;
}

export interface SupplierControlService {
    /** All supplier organizations (with sites + logins). */
    listSuppliers(): Promise<ControlSupplier[]>;
    /** Pending supplier sign-ups awaiting approval. */
    listRequests(): Promise<ControlRequest[]>;
    /**
     * Approve a pending supplier sign-up (enable the account + provision its
     * M2M client). Returns the refreshed pending list.
     */
    approveRequest(requestId: string): Promise<ControlRequest[]>;
    /** Grant or revoke a login's access to a site. */
    setSiteAccess(email: string, siteId: string, granted: boolean): Promise<void>;
    /** All user-pool accounts, classified as supplier vs employee (ops view). */
    listUsers(): Promise<ControlUser[]>;
}

// ── Supplier Insights (internal reporting) ────────────────────────────────────

/** A supplier performance row for the Insights supplier directory. */
export interface InsightsSupplier {
    id: string;
    name: string;
    asns: number;
    /** Validation pass %. */
    valid: number;
    /** Downstream pass %. */
    dsOk: number;
    /** Compliance % (validation AND downstream). */
    compliance: number;
    last: string;
    status: 'Active' | 'Inactive';
}

/** Aggregate metrics for the Insights overview tiles. */
export interface InsightsOverview {
    supplierCount: number;
    asnCount: number;
    onTimePct: number;
    errorRate: number;
}

/** ASN outcome for the ASN-review table. */
export type InsightsOutcome = 'passed' | 'schema' | 'semantic' | 'business';

/** One ASN transaction row in the ASN-review table. */
export interface InsightsTransaction {
    asn: string;
    supplier: string;
    pos: string[];
    date: string;
    outcome: InsightsOutcome;
    error: string;
    lines: number;
    attempts: number;
}

/** A purchase order row for the PO-review table. */
export interface InsightsPurchaseOrder {
    po: string;
    supplier: string;
    lines: number;
    needBy: string;
    status: string;
}

/** A PO-line row for the PO-lines-review table. */
export interface InsightsPoLine {
    po: string;
    line: number;
    item: string;
    supplier: string;
    ordered: number;
    received: number;
    needBy: string;
}

export interface SupplierInsightsService {
    /** Top-line overview metrics. */
    getOverview(): Promise<InsightsOverview>;
    /** Per-supplier performance rows. */
    listSuppliers(): Promise<InsightsSupplier[]>;
    /** ASN transactions across suppliers. */
    listTransactions(): Promise<InsightsTransaction[]>;
    /** Purchase orders. */
    listPurchaseOrders(): Promise<InsightsPurchaseOrder[]>;
    /** PO lines. */
    listPoLines(): Promise<InsightsPoLine[]>;
}
